import { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { Attachment as StreamAttachment } from 'stream-chat';
import { DefaultStreamChatGenerics } from 'stream-chat-react';
import { CategoryFileIcon, DownloadActionIcon } from 'shared/components/Attachments/AttachmentIcons';
import {
	downloadAttachment,
	formatFileSize,
	getAttachmentCategory,
	getAttachmentDisplayName,
} from 'shared/components/Attachments/attachmentUtils';
import styles from './CustomFileAttachment.module.scss';

export interface CustomFileAttachmentProps<
	StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
> {
	attachment: StreamAttachment<StreamChatGenerics>;
}

export const CustomFileAttachment: ComponentType<CustomFileAttachmentProps<DefaultStreamChatGenerics>> = ({
	attachment,
}) => {
	const { t } = useTranslation(['admin']);

	const targetUrl =
		attachment.asset_url ||
		attachment.image_url ||
		attachment.title_link ||
		(attachment as unknown as Record<string, string>).url ||
		'';
	const rawTitle = attachment.title || (attachment as unknown as Record<string, string>).name || targetUrl || '';
	const displayName = getAttachmentDisplayName(rawTitle || targetUrl);
	const category = getAttachmentCategory(targetUrl || rawTitle);
	const parsedSize =
		typeof attachment.file_size === 'number'
			? attachment.file_size
			: typeof attachment.file_size === 'string'
			? parseInt(attachment.file_size, 10) || undefined
			: undefined;
	const fileSizeText = formatFileSize(parsedSize);

	const handleDownload = () => {
		downloadAttachment(targetUrl || rawTitle, displayName);
	};

	return (
		<div
			className={cx(styles.fileCard, 'str-chat__custom-file-attachment')}
			onClick={handleDownload}
			role="button"
			tabIndex={0}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					handleDownload();
				}
			}}
			title={t('admin:click_to_download', 'Click to download {{name}}', { name: displayName })}
		>
			<div className={styles.fileCard_left}>
				<div className={styles.fileCard_iconWrap}>
					<CategoryFileIcon category={category} />
				</div>
				<div className={styles.fileCard_info}>
					<span className={styles.fileCard_name} title={displayName}>
						{displayName}
					</span>
					{fileSizeText && <span className={styles.fileCard_size}>{fileSizeText}</span>}
				</div>
			</div>

			<button
				type="button"
				className={styles.downloadBtn}
				title={t('admin:download', 'Download')}
				aria-label={`Download ${displayName}`}
				onClick={e => {
					e.stopPropagation();
					handleDownload();
				}}
			>
				<DownloadActionIcon />
			</button>
		</div>
	);
};

export default CustomFileAttachment;
